import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProductGallery from './components/ProductGallery';
import RelatedProducts from './components/RelatedProducts';
import ProductReviews from './components/ProductReviews';
import { beverages } from '../../mocks/beverages';
import { beverageDetails } from '../../mocks/beverageDetails';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';

const categoryLabels: Record<string, string> = {
  beer: 'Beer',
  wine: 'Wine',
  spirits: 'Spirits',
  soft: 'Soft Drinks',
  energy: 'Energy Drinks',
  water: 'Water',
};

export default function BeveragePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const toast = useToast();
  const [qty, setQty] = useState(1);
  const [addedPulse, setAddedPulse] = useState(false);

  const bev = beverages.find((b) => b.id === id);
  const detail = id ? beverageDetails[id] : null;

  if (!bev) {
    return (
      <div className="min-h-screen bg-[#fafaf8] flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-24 pb-16 px-6 text-center">
          <div>
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] mx-auto mb-5">
              <i className="ri-beer-line text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Beverage not found</h2>
            <p className="text-gray-500 text-sm mb-6">This product doesn&apos;t exist or may have been removed.</p>
            <Link to="/menu" className="bg-[#FF6B35] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#e85d2a] transition-colors cursor-pointer whitespace-nowrap">
              Browse Menu
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;
  const allImages = detail ? [bev.image, ...detail.gallery] : [bev.image];

  const handleAddToCart = () => {
    for (let i = 0; i < qty; i++) {
      addItem({ id: bev.id, name: bev.name, price: bev.price, image: bev.image, volume: bev.volume });
    }
    setAddedPulse(true);
    setTimeout(() => setAddedPulse(false), 800);
    toast.cart(
      qty > 1 ? `${qty}× ${bev.name} added!` : `${bev.name} added!`,
      `GYD $${(bev.price * qty).toLocaleString()} · ${bev.volume}`
    );
  };

  const handleBuyNow = () => {
    handleAddToCart();
    setTimeout(() => openCart(), 300);
  };

  const discount = bev.originalPrice
    ? Math.round(((bev.originalPrice - bev.price) / bev.originalPrice) * 100)
    : null;

  return (
    <div className="min-h-screen bg-[#fafaf8] flex flex-col">
      <Navbar />

      <main className="flex-1 pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-5">
          <nav className="flex items-center gap-2 text-xs text-gray-400">
            <Link to="/" className="hover:text-[#FF6B35] cursor-pointer transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-300" />
            <Link to="/menu" className="hover:text-[#FF6B35] cursor-pointer transition-colors">Menu</Link>
            <i className="ri-arrow-right-s-line text-gray-300" />
            <span className="text-gray-500 cursor-pointer hover:text-[#FF6B35] transition-colors"
              onClick={() => navigate(`/menu`)}>
              {categoryLabels[bev.category] || bev.category}
            </span>
            <i className="ri-arrow-right-s-line text-gray-300" />
            <span className="text-gray-800 font-medium truncate max-w-[200px]">{bev.name}</span>
          </nav>
        </div>

        {/* Main detail layout */}
        <div className="max-w-7xl mx-auto px-6 pb-16">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            {/* Left — Gallery (sticky) */}
            <div className="lg:sticky lg:top-24">
              <ProductGallery mainImage={bev.image} extraImages={detail?.gallery ?? []} name={bev.name} />
            </div>

            {/* Right — Info */}
            <div className="flex flex-col gap-6">
              {/* Badge + name */}
              <div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {bev.badge && (
                    <span className="bg-[#FF6B35] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      {bev.badge}
                    </span>
                  )}
                  {discount && (
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      {discount}% OFF
                    </span>
                  )}
                  <span className="bg-gray-100 text-gray-600 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    {categoryLabels[bev.category]}
                  </span>
                </div>
                <h1
                  className="text-4xl font-black text-gray-900 mb-3 leading-tight"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}
                >
                  {bev.name}
                </h1>

                {/* Rating */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className={`w-4 h-4 flex items-center justify-center ${i < Math.floor(bev.rating) ? 'text-amber-400' : i < bev.rating ? 'text-amber-300' : 'text-gray-200'}`}>
                        <i className="ri-star-fill text-sm" />
                      </div>
                    ))}
                  </div>
                  <span className="text-sm font-bold text-gray-800">{bev.rating}</span>
                  <span className="text-sm text-gray-400">({bev.reviews} reviews)</span>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-black text-gray-900">{formatPrice(bev.price)}</span>
                {bev.originalPrice && (
                  <span className="text-xl text-gray-400 line-through">{formatPrice(bev.originalPrice)}</span>
                )}
              </div>

              {/* Description */}
              {detail && (
                <p className="text-gray-600 text-sm leading-relaxed border-l-2 border-[#FF6B35]/30 pl-4">
                  {detail.description}
                </p>
              )}

              {/* Quick info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-scales-line', label: 'Volume', value: bev.volume },
                  { icon: 'ri-time-line', label: 'Delivery', value: bev.deliveryTime },
                  ...(detail?.abv ? [{ icon: 'ri-drop-line', label: 'ABV', value: detail.abv }] : []),
                  ...(detail?.origin ? [{ icon: 'ri-map-pin-2-line', label: 'Origin', value: detail.origin }] : []),
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] flex-shrink-0">
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Highlights */}
              {detail?.highlights && (
                <div className="space-y-2">
                  {detail.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-50 text-green-500 flex-shrink-0">
                        <i className="ri-check-line text-xs" />
                      </div>
                      <span className="text-sm text-gray-700">{h}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Temperature */}
              {detail?.temperature && (
                <div className="flex items-center gap-3 bg-[#FFF5F0] border border-orange-100 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center text-[#FF6B35] flex-shrink-0">
                    <i className="ri-temp-cold-line text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-[#FF6B35] font-semibold uppercase tracking-wide">Serving Suggestion</p>
                    <p className="text-sm text-gray-700">{detail.temperature}</p>
                  </div>
                </div>
              )}

              {/* Tags */}
              {detail?.tags && (
                <div className="flex flex-wrap gap-2">
                  {detail.tags.map((tag) => (
                    <span key={tag} className="text-xs text-gray-500 bg-white border border-gray-200 px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Quantity + Add to Cart */}
              <div className="flex flex-col gap-3 pt-2">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Quantity:</span>
                  <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-full px-2 py-1.5">
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700 transition-colors cursor-pointer font-bold"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <span className="text-base font-black text-gray-900 w-6 text-center select-none">{qty}</span>
                    <button
                      onClick={() => setQty((q) => q + 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    Total: <span className="font-bold text-gray-900">{formatPrice(bev.price * qty)}</span>
                  </span>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleAddToCart}
                    className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap border-2 border-[#FF6B35] ${
                      addedPulse ? 'bg-green-500 border-green-500 text-white scale-95' : 'bg-white text-[#FF6B35] hover:bg-[#FFF5F0]'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={addedPulse ? 'ri-check-line' : 'ri-shopping-cart-line'} />
                    </div>
                    {addedPulse ? 'Added!' : 'Add to Cart'}
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#e85d2a] text-white py-3.5 rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-flashlight-line" />
                    </div>
                    Buy Now
                  </button>
                </div>

                {/* Delivery promise */}
                <div className="flex items-center gap-4 pt-1">
                  {[
                    { icon: 'ri-motorbike-line', text: 'Free delivery' },
                    { icon: 'ri-shield-check-line', text: 'Secure payment' },
                    { icon: 'ri-map-pin-line', text: 'Georgetown only' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-1.5 text-gray-500">
                      <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35]">
                        <i className={`${item.icon} text-xs`} />
                      </div>
                      <span className="text-xs whitespace-nowrap">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pairings */}
              {detail?.pairings && detail.pairings.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-2xl p-5">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                    <i className="ri-restaurant-2-line mr-1.5 text-[#FF6B35]" />
                    Food Pairings
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detail.pairings.map((p) => (
                      <span key={p} className="text-sm text-gray-700 bg-[#FAF8F5] px-3 py-1.5 rounded-lg border border-gray-100">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer Reviews */}
          <ProductReviews
            beverageId={bev.id}
            beverageName={bev.name}
            baseRating={bev.rating}
            baseCount={bev.reviews}
          />

          {/* Related Products */}
          <RelatedProducts currentId={bev.id} category={bev.category} />
        </div>
      </main>

      <Footer />
    </div>
  );
}
